import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  AccessControl,
  AccessControls,
  MuiButton,
  ViewHandlingPanel,
} from "@ryde/controls";
import { useBookingsNavigation } from "@ryde/redux";
import {
  ActionsEnum,
  bookingCalendarSelectedCellsCount,
  DrawerType,
  selectedTableRowsIdsAtomFamily,
  useDrawers,
} from "@ryde/shared";

export const BookingCustomHeaderRight: FC = () => {
  const { t } = useTranslation();
  const { openDrawer } = useDrawers();
  const { isCalendarBookingView } = useBookingsNavigation();

  // Selection states
  const selectedGridIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily("booking"),
  );
  const selectedCalendarCellsCount = useRecoilValue(
    bookingCalendarSelectedCellsCount,
  );

  const isAnySelection = useMemo(() => {
    return isCalendarBookingView
      ? selectedCalendarCellsCount > 0
      : Boolean(selectedGridIds?.length);
  }, [isCalendarBookingView, selectedCalendarCellsCount, selectedGridIds]);

  const entityNameTranslated = t(`booking.single`);

  const onAddANew = (): void => {
    openDrawer({
      drawerProps: {
        type: DrawerType.AddNewBulkBookings,
      },
    });
  };

  const onUploadBookings = (): void => {
    openDrawer({
      drawerProps: {
        type: DrawerType.BulkUploadBookings,
      },
    });
  };

  return (
    <>
      <ViewHandlingPanel />

      <AccessControl entity="booking" action="create">
        <MuiButton
          e2eData="add-entity-button"
          onClick={onAddANew}
          disabled={isAnySelection}
          title={
            isAnySelection ? t("booking.bulk.useBulkPanelHint") : undefined
          }
          sx={{ flexShrink: 0 }}
        >
          {t("general.ADD_A_NEW_ENTITY", { entity: entityNameTranslated })}
        </MuiButton>
      </AccessControl>

      <AccessControls
        actionsList={[
          { entity: "booking", action: ActionsEnum.Create },
          { entity: "system", action: ActionsEnum.AllowImport },
        ]}
      >
        <MuiButton
          e2eData="upload-entity-button"
          onClick={onUploadBookings}
          sx={{ flexShrink: 0 }}
        >
          {t("general.UPLOAD_A_NEW_ENTITY", { entity: entityNameTranslated })}
        </MuiButton>
      </AccessControls>
    </>
  );
};
